import type {
  ApiError,
  ChangeEmailRequest,
  ChangeEmailResponse,
  ChangePasswordRequest,
  PopulateUserRequest,
  ResetPasswordConfirmRequest,
  ResetPasswordResponse
} from "@9amhealth/openapi";
import {
  LillyControllerService,
  LoginRequest,
  Saml2AuthControllerService,
  SoleraControllerService,
  UserControllerService
} from "@9amhealth/openapi";
import { App, AppState } from "@capacitor/app";
import { Blac, Cubit } from "blac-next";
import { processRequestQueue } from "src/api/ApiController";
import { logSentryBreadcrumb } from "src/lib/addSentryBreadcrumb";
import parseJwt from "src/lib/parseJwt";
import reportErrorSentry from "src/lib/reportErrorSentry";
import BiometricCredentialsProvider from "src/state/UserCubit/CredentialProviders/BiometricCredentialsProvider";
import LocalStorageTokenCredentialsProvider from "src/state/UserCubit/CredentialProviders/LocalStorageTokenCredentialsProvider";
import type CredentialsProvider from "src/state/UserCubit/CredentialsProvider";
import type { ProviderCredentials } from "src/state/UserCubit/CredentialsProvider";
import BlockingLoadingOverlayController from "src/ui/components/BlockingLoadingOverlay/BlockingLoadingOverlayController";
import Glp1RefillBloc from "src/ui/components/Gpl1RefillForm/Glp1RefillBloc";
import { AuthenticationMethod } from "src/ui/components/WithAuth/WithAuthCubit";
import { globalEvents } from "../../constants/globalEvents";
import type { AuthStage } from "../../types/auth";
import { LoadingKey } from "../LoadingCubit/LoadingCubit";
import QuestionnaireCubit from "../QuestionnaireCubit/QuestionnaireCubit";
import {
  apiMiddleware,
  appViewState,
  healthSyncState,
  loadingState,
  pushNotificationState,
  subscriptionState,
  tracker,
  userPreferences,
  userState
} from "../state";
import { StorageController } from "../StorageBloc/StorageBloc";
import { UserPreferenceKeys } from "../UserPreferencesCubit/UserPreferencesCubit";
import WebSocketBloc from "../WebSocketBloc/WebSocketBloc";
import { MAX_TIMEOUT } from "src/constants/misc";
import { FeatureFlagName, featureFlags } from "src/lib/featureFlags";
import { ErrorCode } from "src/constants/errorCodes";

type AuthenticationBlocState = {
  authenticationStatus: "authenticated" | "pending" | "unauthenticated";
  credentials: ProviderCredentials;
  authStage: AuthStage;
};

const biometricsProvider = new BiometricCredentialsProvider();
const websocketState = Blac.getBloc(WebSocketBloc);

export default class AuthenticationBloc extends Cubit<AuthenticationBlocState> {
  isTempUser = false;
  externalLoginInProgress = false;
  private selectedUserId: string | null = null;

  // Credential providers
  availableProviders = new Set<CredentialsProvider>([biometricsProvider]);
  biometricsProvider = biometricsProvider;
  tokenProvider = new LocalStorageTokenCredentialsProvider();
  primaryProvider?: CredentialsProvider;

  // Inflight request deduplication
  private initialCredentialsRequest?: Promise<void>;
  private refreshAccessTokenRequest?: Promise<void>;
  private createAnonymousAccountRequest?: Promise<void>;

  constructor() {
    super({
      authenticationStatus: "unauthenticated",
      credentials: {},
      authStage: "nullAuth"
    });
    const fullSerach = new URLSearchParams(window.location.search);
    const tokenInUrl = fullSerach.get("token") ?? fullSerach.get("lookupKey");

    if (!tokenInUrl) {
      // First select user from storage, then get credentials
      this.selectUserIdFromStorage();
      this.initialCredentialsRequest = this.getCredentials();
    }

    this.listenAppStatus();

    window.addEventListener(globalEvents.USER_CLEAR, () => {
      this.log("USER_CLEAR");
      this.emit({
        authenticationStatus: "unauthenticated",
        credentials: {},
        authStage: "nullAuth"
      });
    });

    window.addEventListener(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      globalEvents.SET_CREDENTIALS as any,
      (event: CustomEvent<ProviderCredentials & { testMode?: boolean }>) => {
        this.log("SET_CREDENTIALS", { hasCredentials: !!event.detail });

        // Only handle external credential injection (with payload)
        // Internal dispatches for state clearing don't have credentials
        if (!event.detail) {
          this.log(
            "SET_CREDENTIALS: no credentials provided, skipping storage clear"
          );
          return;
        }

        const { testMode, ...credentials } = event.detail;

        // Test mode: only update in-memory credentials without storage clear or reload
        // Used for E2E tests to simulate token expiration
        if (testMode) {
          this.log(
            "SET_CREDENTIALS: test mode - updating credentials core only"
          );
          void this.updateCredentialsCore(credentials);
          return;
        }

        // Production mode: full reset with storage clear and reload
        localStorage.clear();
        sessionStorage.clear();
        void this.setCredentials(credentials).then(() => {
          window.location.reload();
        });
      }
    );
  }

  get accessToken(): string | undefined {
    return this.state.credentials.accessToken;
  }

  get refreshToken(): string | undefined {
    return this.state.credentials.refreshToken;
  }

  get authenticationStatus() {
    return this.state.authenticationStatus;
  }

  extractUserId = (): string | undefined => {
    const parsedAccessToken = parseJwt(this.accessToken);
    return parsedAccessToken.sub;
  };

  get activeUserId(): string | null {
    // Always prioritize the user from access token if available
    const tokenUserId = this.extractUserId();
    if (tokenUserId) {
      this.log("activeUserId from token", { tokenUserId });
      return tokenUserId;
    }

    // Only use selected user if no valid access token
    this.log("activeUserId from selected", {
      selectedUserId: this.selectedUserId
    });
    return this.selectedUserId;
  }

  private categorizeAuthStage(authStage: string | null): AuthStage {
    switch (authStage) {
      case "fullAuth":
      case "partialAuth":
      case "anonymous":
      case "noAuth":
        return authStage;
      default:
        return "nullAuth";
    }
  }

  private validateAndParseRefreshToken(
    token: string,
    userId: string
  ): { sub: string } | null {
    try {
      const parsedToken = parseJwt(token);
      if (!parsedToken.sub) {
        this.log("Invalid or missing JWT subject for user", userId);
        return null;
      }
      return { sub: parsedToken.sub };
    } catch (error) {
      this.log("Failed to parse JWT for user", { userId, error });
      reportErrorSentry(
        new Error(`JWT parsing failed for user ${userId}`, { cause: error })
      );
      return null;
    }
  }

  private findBestUserCandidate(
    latestWithAccess: { userId: string; time: number; storage?: Storage },
    latestByAuthStage: Record<
      AuthStage,
      { userId: string; time: number; storage?: Storage }
    >
  ): { userId: string; storage: Storage } | null {
    if (latestWithAccess.userId && latestWithAccess.storage) {
      return {
        userId: latestWithAccess.userId,
        storage: latestWithAccess.storage
      };
    }

    const authStagePriority: AuthStage[] = [
      "fullAuth",
      "partialAuth",
      "anonymous",
      "noAuth",
      "nullAuth"
    ];

    for (const stage of authStagePriority) {
      if (latestByAuthStage[stage].userId && latestByAuthStage[stage].storage) {
        return {
          userId: latestByAuthStage[stage].userId,
          storage: latestByAuthStage[stage].storage
        };
      }
    }

    return null;
  }

  private processUserCandidate(
    lastActiveKey: string,
    latestWithAccess: { userId: string; time: number; storage?: Storage },
    latestByAuthStage: Record<
      AuthStage,
      { userId: string; time: number; storage?: Storage }
    >
  ): void {
    const storageType =
      localStorage.getItem(lastActiveKey) !== null
        ? localStorage
        : sessionStorage;
    const activeTime = parseInt(storageType.getItem(lastActiveKey) ?? "0", 10);
    const userIdRegex = StorageController.createPatternForKey("lastActive");
    const userId = lastActiveKey.match(userIdRegex)?.[1];

    this.log("Processing user candidate", {
      lastActiveKey,
      storageType:
        storageType === localStorage ? "localStorage" : "sessionStorage",
      activeTime,
      userId
    });

    if (!userId) {
      this.log("No userId found in key", { lastActiveKey });
      return;
    }

    // Check for refresh token in the same storage where lastActive was found
    const hasRefreshToken = storageType.getItem(`9am-${userId}-refreshToken`);
    if (!hasRefreshToken) {
      this.log("No refresh token for user in expected storage", {
        userId,
        expectedStorage:
          storageType === localStorage ? "localStorage" : "sessionStorage"
      });
      return;
    }

    const parsedRefreshToken = this.validateAndParseRefreshToken(
      hasRefreshToken,
      userId
    );
    if (!parsedRefreshToken) {
      this.log("Invalid refresh token for user", { userId });
      return;
    }

    // Check for app access in the same storage where lastActive was found
    const hasAppAccess =
      storageType.getItem(`9am-${userId}-hasAppAccess`) === "true";

    this.log("User candidate evaluation", {
      userId: parsedRefreshToken.sub,
      hasAppAccess,
      activeTime,
      storageType:
        storageType === localStorage ? "localStorage" : "sessionStorage"
    });

    if (hasAppAccess) {
      if (activeTime > latestWithAccess.time) {
        this.log("New best user with app access", {
          previousUserId: latestWithAccess.userId,
          newUserId: parsedRefreshToken.sub,
          previousTime: latestWithAccess.time,
          newTime: activeTime
        });
        latestWithAccess.userId = parsedRefreshToken.sub;
        latestWithAccess.time = activeTime;
        latestWithAccess.storage = storageType;
      }
    } else {
      // Check for auth stage in the same storage where lastActive was found
      const authStage = storageType.getItem(`9am-${userId}-authStage`);
      const category = this.categorizeAuthStage(authStage);

      this.log("User auth stage categorization", {
        userId: parsedRefreshToken.sub,
        authStage,
        category,
        storageType:
          storageType === localStorage ? "localStorage" : "sessionStorage"
      });

      if (activeTime > latestByAuthStage[category].time) {
        this.log("New best user for auth stage", {
          category,
          previousUserId: latestByAuthStage[category].userId,
          newUserId: parsedRefreshToken.sub,
          previousTime: latestByAuthStage[category].time,
          newTime: activeTime
        });
        latestByAuthStage[category].userId = parsedRefreshToken.sub;
        latestByAuthStage[category].time = activeTime;
        latestByAuthStage[category].storage = storageType;
      }
    }
  }

  selectBestUserFromStorage(): { userId: string; storage: Storage } | null {
    this.log("selectBestUserFromStorage - starting search");
    const localStorageKeys = Object.keys(localStorage);
    const sessionStorageKeys = Object.keys(sessionStorage);
    const allStorageKeys = [...localStorageKeys, ...sessionStorageKeys];
    const userIdRegexLastActive =
      StorageController.createPatternForKey("lastActive");
    const lastActivekeys = allStorageKeys.filter((k) =>
      k.match(userIdRegexLastActive)
    );

    this.log("Found lastActive keys", {
      totalKeys: allStorageKeys.length,
      lastActiveKeys: lastActivekeys.length,
      keys: lastActivekeys
    });

    const latestWithAccess = {
      userId: "",
      time: 0,
      storage: undefined as Storage | undefined
    };
    const latestByAuthStage: Record<
      AuthStage,
      { userId: string; time: number; storage?: Storage }
    > = {
      fullAuth: { userId: "", time: 0, storage: undefined },
      partialAuth: { userId: "", time: 0, storage: undefined },
      anonymous: { userId: "", time: 0, storage: undefined },
      noAuth: { userId: "", time: 0, storage: undefined },
      nullAuth: { userId: "", time: 0, storage: undefined }
    };

    for (const lastActiveKey of lastActivekeys) {
      this.processUserCandidate(
        lastActiveKey,
        latestWithAccess,
        latestByAuthStage
      );
    }

    const result = this.findBestUserCandidate(
      latestWithAccess,
      latestByAuthStage
    );
    this.log("selectBestUserFromStorage - result", {
      result,
      latestWithAccess,
      latestByAuthStage
    });
    return result;
  }

  getUserIdFromStoredRefreshToken(): string | null {
    this.log("getUserIdFromStoredRefreshToken");
    const userIdRegexRefreshToken =
      StorageController.createPatternForKey("refreshToken");
    const allStorageKeys = Object.keys(localStorage);
    const refreshKeys = allStorageKeys.filter((k) =>
      k.match(userIdRegexRefreshToken)
    );
    if (refreshKeys.length > 0) {
      const userId = refreshKeys[0].match(userIdRegexRefreshToken)?.[1];
      if (userId) {
        return userId;
      }
    }

    return null;
  }

  selectUserIdFromStorage(): void {
    this.log("selectUserIdFromStorage");

    // Check if StorageController already has an active user from early initialization
    if (StorageController.activeUserId) {
      this.log("StorageController already has active user", {
        activeUserId: StorageController.activeUserId
      });
      this.selectedUserId = StorageController.activeUserId;
      return;
    }

    // Use StorageController's unified user selection
    const selectedUserId = StorageController.selectAndActivateBestUser();

    if (selectedUserId) {
      this.log("Selected user from StorageController", { selectedUserId });
      this.selectedUserId = selectedUserId;
    } else {
      this.log("No user found in storage");
      this.selectedUserId = null;
    }

    // Update the storage active user ID (in case it wasn't set)
    this.updateStorageActiveUserId();
    this.log("Selected user from storage", { selectedUserId });
  }

  private updateStorageActiveUserId(): void {
    const { activeUserId } = this;
    const previousActiveUserId = StorageController.activeUserId;
    this.log("Updating StorageController activeUserId", {
      activeUserId,
      previousActiveUserId
    });
    StorageController.setActiveUserId(activeUserId);

    // Log active time whenever a user is selected
    if (activeUserId) {
      StorageController.logActive();
      // This handles cases where storage was used before authentication
      if (previousActiveUserId === null && activeUserId) {
        this.log("Migrating temporary storage for newly authenticated user");
        StorageController.migrateTemporaryStorageToUser(activeUserId);
      }
    }
  }

  get loggingEnabled(): boolean {
    return featureFlags.getFlag(FeatureFlagName.loggingAuthenticationManager);
  }

  private readonly log = (message: string, etc?: unknown): void => {
    if (this.loggingEnabled) {
      // eslint-disable-next-line no-console
      console.warn(`[CRED]: ${message}`, etc ?? "");
    }
    logSentryBreadcrumb("credentialProvider", [message, etc]);
  };

  /**
   * Re-evaluate WebSocket connection based on current state
   * Connects WebSocket only if:
   * 1. Has access token AND
   * 2. Has app access (set by UnfinishedSignupOverlay or user state)
   *
   * Note: Campaign completion is checked by UnfinishedSignupOverlay,
   * which sets hasAppAccess when the user has completed required steps.
   */
  recheckWebSocketConnection = async (): Promise<void> => {
    const { accessToken } = this;

    if (!accessToken) {
      websocketState.disconnect();
      return;
    }

    const storageHasAppAccess = StorageController.getHasAppAccess();
    const userStateHasAppAccess = userState.hasAppAccess();
    const hasAppAccess = storageHasAppAccess || userStateHasAppAccess;

    this.log("WebSocket connection decision", {
      hasAccessToken: Boolean(accessToken),
      hasAppAccess
    });

    if (accessToken && hasAppAccess) {
      void websocketState.connectAndAuthenticate(accessToken);
    } else {
      websocketState.disconnect();
    }
  };

  listenAppStatus = () => {
    void App.addListener("appStateChange", this.handleAppStateChange);
  };

  handleAppStateChange = (state: AppState) => {
    const { isActive } = state;
    if (isActive) {
      void this.getCredentials();
    }
  };

  async selectProvider(): Promise<void> {
    this.log("selectProvider", {
      availableProviders: this.availableProviders,
      primaryProvider: this.primaryProvider
    });

    if (this.primaryProvider) {
      return;
    }

    // select first available provider as primary
    for (const provider of this.availableProviders) {
      const available = await provider.isAvailable();
      if (available) {
        this.log("selectProvider: setting primaryProvider", provider);
        this.primaryProvider = provider;
        return;
      } else {
        this.log("selectProvider: provider not available", provider);
      }
    }
  }

  async getCredentials(): Promise<void> {
    if (this.externalLoginInProgress) {
      this.log("getCredentials: external login in progress — skipping");
      return;
    }
    this.log("getCredentials started");
    await this.selectProvider();
    let tokenCredentials: ProviderCredentials | undefined;
    let loginCredentials: ProviderCredentials | undefined;
    const primaryEnabled = await this.primaryProvider?.isEnabled();

    // 0. check primary
    if (primaryEnabled) {
      try {
        loginCredentials = await this.primaryProvider?.getCredentials();
      } catch (error) {
        reportErrorSentry(error);
      }
    }

    this.log("primaryProvider", {
      p: this.primaryProvider,
      primaryEnabled,
      loginCredentials
    });

    // if user has failed primary verification, clear credentials, this will show the login form
    if (
      primaryEnabled &&
      (!this.primaryProvider?.isVerified() ||
        !loginCredentials ||
        !loginCredentials.password ||
        !loginCredentials.username)
    ) {
      return;
    }

    // 1. check for tokens saved
    try {
      tokenCredentials = await this.tokenProvider.getCredentials();
    } catch (error) {
      reportErrorSentry(error);
    }

    const validRefreshToken = Boolean(tokenCredentials?.refreshToken);
    const validAccessToken = Boolean(tokenCredentials?.accessToken);

    const refreshTokenChanged =
      validRefreshToken && tokenCredentials?.refreshToken !== this.refreshToken;
    const accessTokenChanged =
      validAccessToken && tokenCredentials?.accessToken !== this.accessToken;

    // set token credentials if they are valid
    if (tokenCredentials && validRefreshToken) {
      this.log(
        "getCredentials: valid tokens. returning token credentials and setting token"
      );
      if (refreshTokenChanged || accessTokenChanged) {
        await this.setCredentials(tokenCredentials);
      } else {
        this.log(
          "refresh and access token are the same, not setting credentials"
        );
      }
    }

    // check if we have a refresh token
    if (tokenCredentials && !validAccessToken && validRefreshToken) {
      await this.refreshAccessToken();
    }

    // if there are no tokens, but we have login credentials, try to login
    if (
      !validRefreshToken &&
      loginCredentials?.username &&
      loginCredentials.password
    ) {
      this.log(
        "getCredentials: no tokens, but login credentials. trying to login"
      );
      try {
        await this.login({
          email: loginCredentials.username,
          password: loginCredentials.password
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      }
    }
  }

  handleTokenRefreshFailure = (): void => {
    BlockingLoadingOverlayController.endAllLoadings();
    loadingState.finishAllLoadings();
  };

  refreshAccessToken = async (): Promise<void> => {
    if (this.externalLoginInProgress) {
      this.log("refreshAccessToken: external login in progress — skipping");
      return Promise.resolve();
    }
    if (this.refreshAccessTokenRequest) {
      this.log("refreshAccessToken: already running");
      return this.refreshAccessTokenRequest;
    }

    const request = new Promise<void>((resolve, reject) => {
      this.log("starting refreshAccessToken");
      if (!this.refreshToken) {
        this.log("refreshAccessToken: no refresh token");
        const noTokenError = new Error("No refresh token");
        Promise.all([this.clearAllUserData(), this.deleteCredentials()])
          .then(() => {
            this.handleTokenRefreshFailure();
            reject(noTokenError);
          })
          .catch((e) => {
            this.log("Error during cleanup when no refresh token", e);
            reject(noTokenError);
          });
        return;
      }

      UserControllerService.refreshToken()
        .then(async (response) => {
          try {
            this.log(
              "refreshAccessToken: got response, updating credentials core"
            );
            // Use core method to avoid circular dependency - no API calls triggered
            await this.updateCredentialsCore({
              accessToken: response.data.accessToken,
              refreshToken: this.refreshToken
            });
            this.log(
              "refreshAccessToken: credentials updated, resolving promise"
            );
            resolve();
          } catch (e: unknown) {
            this.log("refreshAccessToken: error updating credentials", e);
            reject(e as Error);
          }
        })
        .catch(async (e: unknown) => {
          this.log("refreshAccessToken: failed", e);
          try {
            await Promise.all([
              this.clearAllUserData(),
              this.deleteCredentials()
            ]);
            this.handleTokenRefreshFailure();
          } catch (cleanupError) {
            // Log but don't block rejection
            this.log("Error during cleanup after failed refresh", cleanupError);
          }
          reject(e as Error);
        })
        .finally(() => {
          this.refreshAccessTokenRequest = undefined;
        });
    });

    request.catch(() => {
      this.handleTokenRefreshFailure();
    });

    this.refreshAccessTokenRequest = request;
    return request;
  };

  private refreshTimeout?: number;
  scheduleAutoRefresh = (): void => {
    const parsedAccessToken = parseJwt(this.accessToken);
    const timeTillAuthTokenExpires = parsedAccessToken.expiresInMs;
    const prematureRefreshTime = 60 * 1000 * 1; // 1 minute
    const ttl = Math.max(timeTillAuthTokenExpires - prematureRefreshTime, 0);
    // cap timeout to 32 bit int, because setTimeout can't handle more
    const cap = Math.min(ttl, MAX_TIMEOUT);

    if (this.refreshTimeout) {
      clearTimeout(this.refreshTimeout);
    }

    this.refreshTimeout = window.setTimeout(() => {
      if (!this.externalLoginInProgress) {
        void this.refreshAccessToken();
      } else {
        this.log(
          "scheduleAutoRefresh: suppressed by external login in progress"
        );
      }
    }, cap);

    this.log("scheduleAutoRefresh", {
      prematureRefreshTime,
      cap,
      refreshesAt: new Date(Date.now() + cap),
      expiresAt: new Date(Date.now() + timeTillAuthTokenExpires),
      timeTillAuthTokenExpires
    });
  };

  /**
   * Core credential update logic - updates state and storage only.
   * Does NOT trigger any API calls or side effects that depend on credentials.
   * Use this for token refresh to avoid circular dependencies.
   */
  private async updateCredentialsCore(
    newCredentials: ProviderCredentials
  ): Promise<{
    previousUserId: string | undefined;
    newUserId: string | undefined;
    authenticationStatus: "authenticated" | "pending" | "unauthenticated";
  }> {
    const previousUserId = this.extractUserId();

    this.log("updateCredentialsCore - starting", {
      hasAccessToken: Boolean(newCredentials.accessToken),
      hasRefreshToken: Boolean(newCredentials.refreshToken),
      previousUserId
    });

    await this.selectProvider();
    const credentials = {
      ...this.state.credentials,
      ...newCredentials
    };

    // Clear selected user ID when setting new credentials
    // This ensures the access token takes precedence
    if (credentials.accessToken || credentials.refreshToken) {
      // Check for and log token/selected user mismatch
      if (credentials.accessToken && this.selectedUserId) {
        const tokenUserId = parseJwt(credentials.accessToken)?.sub;
        if (tokenUserId && tokenUserId !== this.selectedUserId) {
          this.log(
            "Token user mismatch with selected user - clearing selected user",
            {
              tokenUserId,
              selectedUserId: this.selectedUserId
            }
          );
        }
      }
      this.log("Clearing selectedUserId - token takes precedence", {
        previousSelectedUserId: this.selectedUserId
      });
      this.selectedUserId = null;
    }

    let authenticationStatus: typeof this.state.authenticationStatus =
      "unauthenticated";

    if (credentials.accessToken) {
      authenticationStatus = "authenticated";
    } else if (credentials.refreshToken) {
      authenticationStatus = "pending";
    }

    this.emit({
      ...this.state,
      credentials,
      authenticationStatus
    });

    this.log("updateCredentialsCore - emitted state", {
      authenticationStatus
    });

    // Log user change for debugging
    const newUserId = this.extractUserId();
    if (newUserId !== previousUserId) {
      this.log("Authenticated user changed", { previousUserId, newUserId });
      this.updateStorageActiveUserId();
    }

    // Save credentials to providers (storage only, no API calls)
    const setCredentialsPromise =
      this.tokenProvider.setCredentials(credentials);

    try {
      await Promise.all([
        this.primaryProvider?.setCredentials(credentials),
        setCredentialsPromise
      ]);
      if (credentials.accessToken) {
        // Process queued requests with new token
        void processRequestQueue();
      }
    } catch (error) {
      reportErrorSentry(error);
    }

    this.log("updateCredentialsCore - completed", {
      authenticationStatus,
      newUserId
    });

    return { previousUserId, newUserId, authenticationStatus };
  }

  async setCredentials(newCredentials: ProviderCredentials): Promise<void> {
    if (this.externalLoginInProgress) {
      this.log("setCredentials during external login");
    }
    const currentAccessToken = this.accessToken;

    this.log("setCredentials - starting", {
      hasAccessToken: Boolean(newCredentials.accessToken),
      hasRefreshToken: Boolean(newCredentials.refreshToken)
    });

    // Call core credential update logic (no API calls)
    const { previousUserId, newUserId, authenticationStatus } =
      await this.updateCredentialsCore(newCredentials);

    this.log("setCredentials - core update complete", {
      authenticationStatus
    });

    // Now handle side effects that may trigger API calls
    const { id: currentlyLoadedUserDataId } = userState.state.userData ?? {};
    const userIdChanged = currentlyLoadedUserDataId !== this.activeUserId;
    const credentialsContainEmailPassword = Boolean(
      newCredentials.username && newCredentials.password
    );
    // update external state
    switch (authenticationStatus) {
      case "authenticated": {
        appViewState.setPartnerSession();

        const shouldLoadData =
          !currentlyLoadedUserDataId ||
          userIdChanged ||
          credentialsContainEmailPassword ||
          !currentAccessToken;

        if (shouldLoadData) {
          await userState.loadRequiredAppUserData();
        }

        // Re-check WebSocket connection based on current state
        // Note: This MUST happen after loadRequiredAppUserData because
        // campaign step completion checks depend on loaded user data
        await this.recheckWebSocketConnection();
        break;
      }
      case "pending":
        websocketState.pauseSendingUntilAuthSuccess();
        break;
      case "unauthenticated":
        websocketState.disconnect();
        break;
    }

    await this.updateAuthStage();

    // Notify other Cubits to clear cached state when user changes
    // Only dispatch if authenticated and user actually changed
    if (
      authenticationStatus === "authenticated" &&
      newUserId !== previousUserId
    ) {
      this.log("Dispatching SET_CREDENTIALS for state clearing", {
        previousUserId,
        newUserId
      });
      window.dispatchEvent(new CustomEvent(globalEvents.SET_CREDENTIALS));
    }

    // schedule auto refresh
    // External login is complete once credentials are set
    if (this.refreshToken) {
      this.scheduleAutoRefresh();
    }
  }

  async deleteCredentials(): Promise<void> {
    await this.selectProvider();
    await this.setCredentials({
      accessToken: undefined,
      refreshToken: undefined,
      username: undefined,
      password: undefined
    });

    try {
      await Promise.all([
        this.primaryProvider?.deleteCredentials(),
        this.tokenProvider.deleteCredentials()
      ]);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to delete credentials", error);
    }
  }

  public readonly login = async (data: {
    email: string;
    password: string;
    mfa?: string;
  }): Promise<void> => {
    loadingState.start(LoadingKey.login);
    let error: ApiError | undefined;
    try {
      const response = await UserControllerService.login({
        ...data,
        role: LoginRequest.role.USER,
        sessionName: navigator.userAgent,
        mfaToken: data.mfa
      });

      await this.setCredentials({
        accessToken: response.data.accessToken,
        refreshToken: response.data.refreshToken,
        username: data.email,
        password: data.password
      });

      this.log("Authenticated user");
    } catch (e: unknown) {
      error = e as ApiError;
    } finally {
      loadingState.finish(LoadingKey.login);
    }

    if (error) {
      throw error;
    }
  };

  public readonly createAnonymousAccount = async (): Promise<void> => {
    // Deduplication - return existing promise if in-flight
    if (this.createAnonymousAccountRequest) {
      this.log("createAnonymousAccount: already running");
      return this.createAnonymousAccountRequest;
    }

    // Set deduplication promise BEFORE any async operations
    const request = (async () => {
      try {
        if (this.isTempUser) return;
        if (this.refreshToken) return;

        // Safety net - check storage directly in case state isn't updated yet
        const storageRefreshToken = StorageController.getItem("refreshToken");
        if (storageRefreshToken) {
          this.log(
            "createAnonymousAccount: refresh token found in storage, skipping"
          );
          return;
        }

        // Wait for initial credentials to finish loading before deciding to create anonymous account
        if (this.initialCredentialsRequest) {
          this.log("createAnonymousAccount: waiting for credentials to load");
          await this.initialCredentialsRequest;
          // Re-check after credentials loaded
          if (this.refreshToken) {
            this.log(
              "createAnonymousAccount: refresh token found after loading, skipping"
            );
            return;
          }
        }

        if (this.state.authenticationStatus !== "unauthenticated") {
          reportErrorSentry(
            new Error("Creating anonymous account while already authenticated")
          );
        }

        const { data } = await UserControllerService.registerAnonymously();
        this.log("Created anonymous account");
        await this.setCredentials({
          accessToken: data.accessToken,
          refreshToken: data.refreshToken
        });
      } catch (e: unknown) {
        reportErrorSentry(e);
      } finally {
        this.createAnonymousAccountRequest = undefined;
      }
    })();

    this.createAnonymousAccountRequest = request;
    return request;
  };

  public readonly register = async (
    data: {
      email: string;
      password: string;
    },
    funnel?: string
  ): Promise<void> => {
    let error: ApiError | undefined;
    let loginSuccess = false;

    loadingState.start(LoadingKey.register);

    // try to log in user first in case where the account already exists
    try {
      await this.login(data);
      loginSuccess = true;
    } catch (e) {
      const loginError = e as ApiError;
      const errorBody = loginError.body as
        | { code?: number | string }
        | undefined;
      const errorCode = errorBody?.code ? String(errorBody.code) : "";

      if (errorCode === ErrorCode.emailVerificationRequired) {
        this.log("Login failed: email verification required");
        throw loginError;
      }

      // eslint-disable-next-line no-console
      console.info("Failed to login user");
    }

    if (!loginSuccess) {
      try {
        await UserControllerService.register({
          ...data,
          initiateEmailVerification: true
        });
        await userState.acceptConsents();
        await this.setCredentials({
          username: data.email,
          password: data.password,
          accessToken: this.accessToken,
          refreshToken: this.refreshToken
        });

        await userPreferences.updateUserPreferences({
          [UserPreferenceKeys.registerFunnel]: funnel
        });
        apiMiddleware.clearAll();
      } catch (e: unknown) {
        error = e as ApiError;
      }
    }

    loadingState.finish(LoadingKey.register);
    if (error) {
      reportErrorSentry(error);
      this.log("Failed to register user");
      throw error;
    }
  };

  public readonly resetPassword = async (postData: {
    email: string;
  }): Promise<ResetPasswordResponse> => {
    let error: ApiError | undefined;
    loadingState.start(LoadingKey.requestPassReset);
    let response: ResetPasswordResponse | undefined;

    try {
      const { data } =
        await UserControllerService.initiatePasswordReset(postData);
      response = data;
      this.log("Successfully reset password");
    } catch (e: unknown) {
      error = e as ApiError;
      this.log("Failed to reset password");
    }

    loadingState.finish(LoadingKey.requestPassReset);

    if (error) {
      throw error;
    }
    if (!response) {
      throw new Error("No response");
    }

    return response;
  };

  public readonly changePassword = async (
    data: ChangePasswordRequest
  ): Promise<void> => {
    loadingState.start(LoadingKey.changePassword);
    let error: ApiError | undefined;

    try {
      await UserControllerService.changePassword(data);
      this.log("Successfully changed password");
    } catch (e: unknown) {
      reportErrorSentry(e);
      error = e as ApiError;
    }
    loadingState.finish(LoadingKey.changePassword);

    if (error) {
      throw error;
    }
  };

  public readonly changeEmail = async (
    changeEmailRequest: ChangeEmailRequest
  ): Promise<ChangeEmailResponse> => {
    apiMiddleware.clearAll();
    let response: ChangeEmailResponse | undefined;
    let error: ApiError | undefined;
    try {
      const { data } =
        await UserControllerService.changeEmail(changeEmailRequest);
      response = data;
      this.log("Successfully changed email");
    } catch (e: unknown) {
      reportErrorSentry(e);
      error = e as ApiError;
    }
    loadingState.finish(LoadingKey.verifyEmail);

    if (error) {
      throw error;
    }
    if (!response) {
      throw new Error("No response");
    }
    return response;
  };

  public readonly resetPasswordVerify = async (
    formData: ResetPasswordConfirmRequest
  ): Promise<void> => {
    loadingState.start(LoadingKey.updatePass);
    let error: ApiError | undefined;
    try {
      await UserControllerService.confirmPasswordReset(formData);
      this.log("Successfully changed password");
    } catch (e: unknown) {
      reportErrorSentry(e);
      error = e as ApiError;
    }
    loadingState.finish(LoadingKey.updatePass);

    if (error) {
      throw error;
    }
  };

  public readonly logout = async (): Promise<void> => {
    const hasToken = Boolean(this.accessToken);
    BlockingLoadingOverlayController.startLoading({
      bg: "branded"
    });

    if (hasToken) {
      try {
        await healthSyncState.unregisterDevice();
        await pushNotificationState.unregister();
        await UserControllerService.logout();
        await Promise.all([
          this.primaryProvider?.deleteCredentials(),
          this.tokenProvider.deleteCredentials()
        ]);
        websocketState.disconnect();
        void this.deleteCredentials();
        void this.clearAllUserData();
        Blac.getBloc(QuestionnaireCubit).clearCache();
      } catch (e) {
        reportErrorSentry(e);
      }
    }

    BlockingLoadingOverlayController.endLoading();
  };

  clearAllUserData = async (): Promise<void> => {
    this.log("clearAllUserData - starting", {
      previousSelectedUserId: this.selectedUserId,
      previousActiveUserId: this.activeUserId
    });
    tracker.resetMixpanelUser();
    window.dispatchEvent(new CustomEvent(globalEvents.USER_CLEAR));
    apiMiddleware.clearAll();
    userState.reset();
    userPreferences.reset();
    subscriptionState.reset();
    Blac.getBloc(Glp1RefillBloc).reset();
    this.selectedUserId = null;
    this.updateStorageActiveUserId();
    this.log("clearAllUserData - completed");
  };

  reportFaultyAccessToken = async (): Promise<void> => {
    this.log("reportFaultyAccessToken");
    return this.refreshAccessToken();
  };

  sendHandoverTokenLilly = async (options: {
    token: string;
    type: PopulateUserRequest.type;
  }): Promise<void> => {
    const { token, type } = options;
    try {
      await LillyControllerService.populateUser({ token, type });
    } catch (e) {
      reportErrorSentry(e);
    }
  };

  sendHandoverTokenSolera = async (options: {
    token: string;
  }): Promise<void> => {
    const { token } = options;
    try {
      await SoleraControllerService.populateSoleraUser({ enrollmentId: token });
    } catch (e) {
      reportErrorSentry(e);
    }
  };

  authWithSamlToken = async (token: string): Promise<void> => {
    try {
      const decodedToken = parseJwt(token);
      const reg = decodedToken.samlreg ?? "";
      this.isTempUser = false;
      userState.isTempUser = false;
      appViewState.setPartnerSource(reg);
    } catch (e) {
      reportErrorSentry(e);
    }

    const authData = await Saml2AuthControllerService.exchange({ token });
    this.externalLoginInProgress = false;
    await this.setCredentials({
      accessToken: authData.data.accessToken,
      refreshToken: authData.data.refreshToken
    });
  };

  /**
   * Prepare for external token-based login (OTT / SAML).
   * Hard reset timers, inflight refresh, websocket, auth state and storage.
   */
  prepareForExternalTokenLogin = (): void => {
    this.log("prepareForExternalTokenLogin");
    this.externalLoginInProgress = true;

    // Stop auto-refresh timer
    if (this.refreshTimeout) {
      clearTimeout(this.refreshTimeout);
      this.refreshTimeout = undefined;
    }

    // Nullify any inflight requests
    this.refreshAccessTokenRequest = undefined;
    this.createAnonymousAccountRequest = undefined;
    this.initialCredentialsRequest = undefined;

    this.emit({
      ...this.state,
      authenticationStatus: "unauthenticated",
      credentials: {},
      authStage: "nullAuth"
    });

    // Clear all users across storages
    try {
      StorageController.clearAllUsers();
    } catch (e) {
      reportErrorSentry(e);
    }
  };

  applyQueryParams = (): void => {};

  updateAuthStage = async (): Promise<void> => {
    if (!this.accessToken) {
      this.emit({ ...this.state, authStage: "noAuth" });
      return;
    }

    await userState.loadUserInfo();
    const { authenticationStatus } = this;
    const { userData } = userState.state;

    function detectAuthStage(): AuthStage {
      if (
        authenticationStatus === "authenticated" &&
        userData &&
        userData.email === undefined &&
        userData.passwordSet === false
      ) {
        return "anonymous";
      } else if (
        authenticationStatus === "authenticated" &&
        userData &&
        (userData.email === undefined || userData.passwordSet === false)
      ) {
        if (userData.email === undefined) {
          return "partialAuth";
        }

        // If the user has a sessionAuthenticationMethod, we consider it fullAuth, does not need password
        const authMethod = userData.sessionAuthenticationMethod;
        if (
          authMethod === AuthenticationMethod.TRANSCARENT_SSO ||
          authMethod === AuthenticationMethod.SAML2
        ) {
          return "fullAuth";
        }
      } else if (authenticationStatus === "authenticated" && userData) {
        return "fullAuth";
      }
      return "noAuth";
    }

    const authStage = detectAuthStage();

    const { StorageController } = await import("../StorageBloc/StorageBloc");
    StorageController.setAuthStage(authStage);

    this.emit({ ...this.state, authStage });

    // Re-check WebSocket connection after auth stage changes
    await this.recheckWebSocketConnection();
  };
}
